import React, { useEffect, useMemo, useState } from "react";
import {
  IonContent,
  IonPage,
  IonCard,
  IonCardContent,
  IonItem,
  IonLabel,
  IonIcon,
  IonList,
  IonButton,
  IonAvatar,
  IonText,
  IonAlert,
  IonRefresher,
  IonRefresherContent,
  IonSkeletonText,
  RefresherEventDetail,
  IonInput,
  IonToast,
  IonModal,
  IonSpinner,
} from "@ionic/react";
import {
  mailOutline,
  businessOutline,
  lockClosedOutline,
  informationCircleOutline,
  logOutOutline,
  shieldCheckmarkOutline,
  calendarOutline,
  checkmarkOutline,
  peopleOutline,
} from "ionicons/icons";
import {
  useLogout,
  useChangePassword,
  useAuth,
  useOrganisations,
  useSwitchOrganisation,
} from "@/hooks";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getCurrentUser } from "@/api";
import "./Profile.css";
import axios from "axios";
import { report } from "@services/Sentry.ts";
import { AppHeader } from "@/components/AppHeader";
import OrganisationsInvitationsModal from "@components/Modals/OrganisationsInvitationsModal.tsx";
import { SelectOrganisationInput } from "@components/SelectOrganisation.tsx";
import {
  APP_CREATOR,
  APP_CREATOR_WEBSITE,
  APP_NAME,
  APP_VERSION,
  COPYRIGHT_TEXT,
} from "@/config/config.ts";

const Profile: React.FC = () => {
  const logout = useLogout();
  const changePassword = useChangePassword();
  const [showLogoutAlert, setShowLogoutAlert] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showOrganisationsModal, setShowOrganisationsModal] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [passwordForm, setPasswordForm] = useState({
    current_password: "",
    password: "",
    password_confirmation: "",
  });
  const { currentOrganisation } = useAuth();
  const { data: organisationData } = useOrganisations();

  const organisations = useMemo(() => {
    return organisationData?.organisations ?? [];
  }, [organisationData?.organisations]);

  const [passwordError, setPasswordError] = useState("");

  const { mutateAsync: switchOrganisationAsync, mutate: switchOrganisation } =
    useSwitchOrganisation();

  useEffect(() => {
    if (currentOrganisation) {
      return;
    }

    if (!organisations.length) {
      return;
    }

    switchOrganisationAsync(organisations[0].id).catch(() => {});
  }, [currentOrganisation, organisations, switchOrganisationAsync]);

  const {
    data: user,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["user", "profile"],
    queryFn: getCurrentUser,
  });

  const queryClient = useQueryClient();

  const handleRefresh = async (event: CustomEvent<RefresherEventDetail>) => {
    await refetch();
    await queryClient.refetchQueries({ queryKey: ["organisations"] });
    event.detail.complete();
  };

  const handleLogout = async () => {
    await logout.mutateAsync();
  };

  const handleChangePassword = async () => {
    setPasswordError("");

    // Basic validation
    if (
      !passwordForm.current_password ||
      !passwordForm.password ||
      !passwordForm.password_confirmation
    ) {
      setPasswordError("Alle velden zijn verplicht");
      return;
    }

    if (passwordForm.password !== passwordForm.password_confirmation) {
      setPasswordError("Nieuwe wachtwoorden komen niet overeen");
      return;
    }

    if (passwordForm.password.length < 8) {
      setPasswordError("Nieuw wachtwoord moet minimaal 8 karakters lang zijn");
      return;
    }

    try {
      await changePassword.mutateAsync(passwordForm);
      setToastMessage("Wachtwoord succesvol gewijzigd");
      setShowToast(true);
      setShowPasswordModal(false);
      setPasswordForm({
        current_password: "",
        password: "",
        password_confirmation: "",
      });
      setPasswordError("");
    } catch (error: unknown) {
      let errorMessage = "Er ging iets mis bij het wijzigen van je wachtwoord";
      if (axios.isAxiosError(error) && error?.response?.data?.message) {
        errorMessage = error?.response?.data?.message;
      } else {
        report(error);
      }
      setPasswordError(errorMessage);
    }
  };

  const closePasswordModal = () => {
    setShowPasswordModal(false);
    setPasswordForm({
      current_password: "",
      password: "",
      password_confirmation: "",
    });
    setPasswordError("");
  };

  const getInitials = (name?: string) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((part) => part[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("nl-NL", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <IonPage>
      <AppHeader title="Profiel" />

      <IonContent fullscreen>
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent />
        </IonRefresher>

        <div className="profile-container">
          {/* User Info Card */}
          <IonCard className="user-card">
            <IonCardContent>
              <div className="user-header">
                <IonAvatar className="user-avatar">
                  <div className="avatar-placeholder">
                    {isLoading ? (
                      <IonSkeletonText animated />
                    ) : (
                      getInitials(user?.name)
                    )}
                  </div>
                </IonAvatar>
                <div className="user-info">
                  {isLoading ? (
                    <>
                      <IonSkeletonText
                        animated
                        style={{ width: "150px", height: "24px" }}
                      />
                      <IonSkeletonText
                        animated
                        style={{
                          width: "100px",
                          height: "16px",
                          marginTop: "4px",
                        }}
                      />
                    </>
                  ) : (
                    <>
                      <h2>{user?.name}</h2>
                      <IonText color="light">
                        <p>
                          {user?.role === "admin"
                            ? "Administrator"
                            : "Medewerker"}
                        </p>
                      </IonText>
                    </>
                  )}
                </div>
              </div>

              <IonList lines="none" className="user-details">
                <IonItem>
                  <IonIcon icon={mailOutline} slot="start" className="mr-2" />
                  <IonLabel color="light">
                    <p>E-mail</p>
                    {isLoading ? (
                      <IonSkeletonText animated style={{ width: "180px" }} />
                    ) : (
                      <h3>{user?.email}</h3>
                    )}
                  </IonLabel>
                </IonItem>

                {user?.role === "admin" && (
                  <IonItem>
                    <IonIcon
                      icon={shieldCheckmarkOutline}
                      slot="start"
                      color="primary"
                      className="mr-2"
                    />
                    <IonLabel color="light">
                      <p>Toegangsniveau</p>
                      <h3>Administrator</h3>
                    </IonLabel>
                  </IonItem>
                )}

                <IonItem>
                  <IonIcon
                    icon={calendarOutline}
                    slot="start"
                    className="mr-2"
                  />
                  <IonLabel color="light">
                    <p>Lid sinds</p>
                    {isLoading ? (
                      <IonSkeletonText animated style={{ width: "120px" }} />
                    ) : (
                      <h3>{formatDate(user?.created_at)}</h3>
                    )}
                  </IonLabel>
                </IonItem>
              </IonList>
            </IonCardContent>
          </IonCard>

          {/* Organisation Card */}
          <IonCard>
            <IonCardContent>
              <h3 className="card-title">Organisatie</h3>
              <IonList lines="none">
                <SelectOrganisationInput
                  disabled={organisations.length === 0}
                  onOrganisationSelect={(organisation) =>
                    switchOrganisation(organisation.id)
                  }
                  value={currentOrganisation?.id}
                >
                  {({ onClick, disabled }) => (
                    <IonItem
                      button={!disabled}
                      onClick={onClick}
                      disabled={disabled}
                    >
                      <IonIcon
                        icon={businessOutline}
                        slot="start"
                        className="mr-2"
                      />
                      <IonLabel>
                        <p>Huidige organisatie</p>
                        <h3>
                          {currentOrganisation?.name || "Geen organisatie"}
                        </h3>
                      </IonLabel>
                    </IonItem>
                  )}
                </SelectOrganisationInput>
                <IonItem
                  button
                  detail
                  onClick={() => setShowOrganisationsModal(true)}
                  className="my-2"
                >
                  <IonIcon icon={peopleOutline} slot="start" className="mr-2" />
                  <IonLabel>
                    <h3>Organisaties & Uitnodigingen</h3>
                    <p>Bekijk en beheer organisaties</p>
                  </IonLabel>
                </IonItem>
              </IonList>
            </IonCardContent>
          </IonCard>

          {/* Security Card */}
          <IonCard>
            <IonCardContent>
              <h3 className="card-title">Beveiliging</h3>
              <IonList lines="none">
                <IonItem
                  button
                  detail
                  onClick={() => setShowPasswordModal(true)}
                  className="my-2"
                >
                  <IonIcon
                    icon={lockClosedOutline}
                    slot="start"
                    className="mr-2"
                  />
                  <IonLabel>
                    <h3>Wachtwoord wijzigen</h3>
                    <p>Wijzig je wachtwoord</p>
                  </IonLabel>
                </IonItem>

                {user?.email_verified_at && (
                  <IonItem className="my-2">
                    <IonIcon
                      icon={shieldCheckmarkOutline}
                      slot="start"
                      color="success"
                      className="mr-2"
                    />
                    <IonLabel>
                      <h3>E-mail geverifieerd</h3>
                      <p>{formatDate(user.email_verified_at)}</p>
                    </IonLabel>
                  </IonItem>
                )}
              </IonList>
            </IonCardContent>
          </IonCard>

          {/* About Card */}
          <IonCard>
            <IonCardContent>
              <h3 className="card-title">Over</h3>
              <IonList lines="none">
                <IonItem className="py-2">
                  <IonIcon
                    icon={informationCircleOutline}
                    slot="start"
                    className="mr-2"
                  />
                  <IonLabel>
                    <h3>{APP_NAME}</h3>
                    <p>Versie {APP_VERSION}</p>
                  </IonLabel>
                </IonItem>

                <IonItem className="py-2">
                  <IonIcon
                    icon={businessOutline}
                    slot="start"
                    className="mr-2"
                  />
                  <IonLabel>
                    <h3>{APP_CREATOR}</h3>
                    <p>{APP_CREATOR_WEBSITE}</p>
                  </IonLabel>
                </IonItem>
              </IonList>
            </IonCardContent>
          </IonCard>

          {/* Logout Button */}
          <IonButton
            expand="block"
            fill="outline"
            color="danger"
            className="logout-button"
            onClick={() => setShowLogoutAlert(true)}
          >
            <IonIcon icon={logOutOutline} slot="start" />
            Uitloggen
          </IonButton>

          {/* Copyright */}
          <div className="copyright">
            <IonText color="medium">
              <p>{COPYRIGHT_TEXT}</p>
              <p>Alle rechten voorbehouden</p>
            </IonText>
          </div>
        </div>

        {/* Logout Alert */}
        <IonAlert
          isOpen={showLogoutAlert}
          onDidDismiss={() => setShowLogoutAlert(false)}
          header="Uitloggen"
          message="Weet je zeker dat je wilt uitloggen?"
          buttons={[
            {
              text: "Annuleren",
              role: "cancel",
            },
            {
              text: "Uitloggen",
              handler: handleLogout,
              cssClass: "alert-button-danger",
            },
          ]}
        />

        {/* Change Password Modal */}
        <IonModal isOpen={showPasswordModal} onDidDismiss={closePasswordModal}>
          <AppHeader title="Wachtwoord wijzigen" onClose={closePasswordModal} />
          <IonContent>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleChangePassword();
              }}
            >
              <div style={{ padding: "16px" }}>
                {passwordError && (
                  <IonText color="danger">
                    <p style={{ marginBottom: "16px" }}>{passwordError}</p>
                  </IonText>
                )}

                <IonItem className="ion-margin-bottom">
                  <IonLabel position="stacked">Huidig wachtwoord *</IonLabel>
                  <IonInput
                    type="password"
                    name="current_password"
                    required
                    value={passwordForm.current_password}
                    onIonInput={(e) =>
                      setPasswordForm((prev) => ({
                        ...prev,
                        current_password: e.detail.value!,
                      }))
                    }
                  />
                </IonItem>

                <IonItem className="ion-margin-bottom">
                  <IonLabel position="stacked">Nieuw wachtwoord *</IonLabel>
                  <IonInput
                    type="password"
                    name="password"
                    required
                    minlength={8}
                    value={passwordForm.password}
                    onIonInput={(e) =>
                      setPasswordForm((prev) => ({
                        ...prev,
                        password: e.detail.value!,
                      }))
                    }
                  />
                </IonItem>

                <IonItem className="ion-margin-bottom">
                  <IonLabel position="stacked">
                    Bevestig nieuw wachtwoord *
                  </IonLabel>
                  <IonInput
                    type="password"
                    name="password_confirmation"
                    required
                    value={passwordForm.password_confirmation}
                    onIonInput={(e) =>
                      setPasswordForm((prev) => ({
                        ...prev,
                        password_confirmation: e.detail.value!,
                      }))
                    }
                  />
                </IonItem>

                <div style={{ marginTop: "24px", display: "flex", gap: "8px" }}>
                  <IonButton
                    expand="block"
                    fill="clear"
                    type="button"
                    onClick={closePasswordModal}
                  >
                    Annuleren
                  </IonButton>
                  <IonButton
                    expand="block"
                    type="submit"
                    disabled={changePassword.isPending}
                  >
                    {changePassword.isPending ? (
                      <IonSpinner />
                    ) : (
                      <>
                        <IonIcon icon={checkmarkOutline} slot="start" />
                        Wijzigen
                      </>
                    )}
                  </IonButton>
                </div>
              </div>
            </form>
          </IonContent>
        </IonModal>

        {/* Toast for feedback */}
        <IonToast
          isOpen={showToast}
          onDidDismiss={() => setShowToast(false)}
          message={toastMessage}
          duration={3000}
          position="bottom"
          color={toastMessage.includes("succesvol") ? "success" : "danger"}
        />

        {/* Organisations & Invitations Modal */}
        <OrganisationsInvitationsModal
          showModal={showOrganisationsModal}
          handleClose={() => setShowOrganisationsModal(false)}
        />
      </IonContent>
    </IonPage>
  );
};

export default Profile;
